/**
 * MyJobSection — v49.5
 * Public discovery section rendered in Studio > MyMouv (below the MyMouv events).
 * Provides a search bar (title / job / category), a "See all" button, and a
 * horizontal carousel of offers. Cover = PRO's profile picture (denormalized).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Search, ChevronRight, MapPin, Building2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { getMediaUrl } from '../../utils/mediaUrl';
import { JOB_L, contractLabel } from './i18n';

const MyJobSection = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const l = JOB_L[language] || JOB_L.fr;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => { const t = setTimeout(() => setDebounced(q), 300); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/myjob/offers', {
        params: { q: debounced || '', limit: 10, lang: language || 'fr' },
        headers: { 'X-App-Language': language || 'fr' },
      });
      setItems(r.data.items || []);
    } catch (_) { /* silent */ } finally { setLoading(false); }
  }, [debounced, language]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="myjob-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-amber-400" />
          {l.title}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSearch(v => !v)}
            className={`p-2 rounded-full ${showSearch ? 'bg-amber-500 text-black' : 'bg-white/10 hover:bg-white/20'}`}
            aria-label={l.search}
            data-testid="myjob-search-toggle"
          >
            <Search className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/myjob')}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs font-semibold"
            data-testid="myjob-see-all-btn"
          >
            {l.seeAll} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {showSearch && (
        <div className="mb-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={l.search}
            className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-amber-500"
            data-testid="myjob-search-input"
          />
        </div>
      )}

      {loading ? (
        <div className="flex gap-3 overflow-hidden -mx-4 px-4">
          {[1, 2, 3].map(i => <div key={i} className="shrink-0 w-[220px] h-[220px] rounded-2xl bg-white/5 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 rounded-2xl bg-white/5 border border-white/10">
          <Briefcase className="w-10 h-10 mx-auto mb-2 text-gray-500" />
          <p className="text-sm text-gray-400">{l.empty}</p>
        </div>
      ) : (
        <div className="flex overflow-x-auto snap-x snap-mandatory gap-3 -mx-4 px-4 pb-1 scrollbar-hide" data-testid="myjob-carousel">
          {items.map(o => (
            <button
              key={o.id}
              onClick={() => navigate(`/myjob/${o.id}`)}
              className="shrink-0 w-[220px] snap-center bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-2xl overflow-hidden text-left hover:border-amber-500/40 transition-colors"
              data-testid={`myjob-card-${o.id}`}
            >
              <div className="relative h-[120px] bg-gradient-to-br from-amber-700/30 to-orange-800/30">
                {o.cover_url ? (
                  <img src={getMediaUrl(o.cover_url)} alt={o.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Briefcase className="w-10 h-10 text-white/30" /></div>
                )}
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-[10px] font-semibold">{contractLabel(o.contract_type, language)}</span>
              </div>
              <div className="p-2.5 space-y-1">
                <h4 className="font-semibold text-sm leading-tight line-clamp-2">{o.title}</h4>
                {o.company_name && <p className="text-[11px] text-gray-400 flex items-center gap-1"><Building2 className="w-3 h-3" /> {o.company_name}</p>}
                {o.location && <p className="text-[11px] text-gray-400 flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /> {o.location}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyJobSection;
